/**
 * Chart of Accounts — collapsible tree grouped by accountType, with a
 * detail Sheet that lists recent transactions, edit, and archive actions.
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Archive, FolderTree, Pencil, Plus } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import { formatMoney, formatMoneySmart } from "@bizop/shared/currency";
import { formatDate } from "@bizop/shared/dates";
import {
  Button,
  DataTable,
  KpiCard,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { ConfirmPopover } from "../../../components/ConfirmPopover";
import { ExportButton } from "../../../components/export";
import {
  downloadCsv,
  exportFilename,
  toCsv,
  type ExportColumn,
} from "../../../lib/export";
import { useMockData } from "../../../lib/mockMode";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import {
  mockAccounts,
  linesForAccount,
  MOCK_NOW,
  type MockAccount,
  type MockAccountType,
} from "../../../lib/glMock";
import {
  AccountForm,
  type AccountFormValues,
} from "../../../components/forms/AccountForm";
import { AccountTreeNode } from "../../../components/gl/AccountTreeNode";
import { AccountTypeBadge } from "../../../components/gl/AccountTypeBadge";

export const Route = createFileRoute("/_app/finance/accounts")({
  component: ChartOfAccountsPage,
});

const TYPE_TABS: Array<{
  value: "all" | MockAccountType;
  label: string;
}> = [
  { value: "all", label: "All" },
  { value: "asset", label: "Assets" },
  { value: "liability", label: "Liabilities" },
  { value: "equity", label: "Equity" },
  { value: "revenue", label: "Revenue" },
  { value: "expense", label: "Expenses" },
];

function ChartOfAccountsPage() {
  const isMock = useMockData();
  void isMock;

  const [accounts, setAccounts] = usePersistentMockState<MockAccount[]>("accounts", mockAccounts);
  const [tab, setTab] = useState<"all" | MockAccountType>("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  const visible = useMemo(
    () =>
      accounts.filter((a) => (tab === "all" ? true : a.accountType === tab)),
    [accounts, tab],
  );

  const childrenByParent = useMemo(() => {
    const map = new Map<string, MockAccount[]>();
    for (const a of visible) {
      const key = a.parentAccountId ?? "_root";
      const list = map.get(key) ?? [];
      list.push(a);
      map.set(key, list);
    }
    return map;
  }, [visible]);

  const roots = useMemo(
    () =>
      (childrenByParent.get("_root") ?? [])
        .slice()
        .sort((a, b) => a.code.localeCompare(b.code)),
    [childrenByParent],
  );

  const totalsByType = useMemo(() => {
    const map = new Map<MockAccountType, bigint>();
    for (const a of accounts) {
      // Skip parent placeholder so totals don't double-count.
      if (a._id === "acc_1500") continue;
      const prev = map.get(a.accountType) ?? 0n;
      map.set(a.accountType, prev + a.currentBalanceInBaseCents);
    }
    return map;
  }, [accounts]);

  const selected = selectedId
    ? accounts.find((a) => a._id === selectedId) ?? null
    : null;
  const recentTxns = selected
    ? linesForAccount(selected._id, {
        fromDate: MOCK_NOW - 90 * 24 * 60 * 60 * 1000,
        toDate: MOCK_NOW,
      }).slice(0, 25)
    : [];

  function handleCreate(values: AccountFormValues) {
    if (accounts.find((a) => a.code === values.code.trim())) {
      toast.error(`Account code "${values.code}" already exists`);
      return;
    }
    const newAcc: MockAccount = {
      _id: `acc_local_${Date.now()}`,
      code: values.code.trim(),
      name: values.name.trim(),
      accountType: values.accountType,
      parentAccountId: values.parentAccountId,
      currency: values.currency,
      isActive: true,
      description: values.description || null,
      normalBalance:
        values.accountType === "asset" || values.accountType === "expense"
          ? "debit"
          : "credit",
      // Seed the opening balance as the current balance in mock mode so
      // newly-created balance-sheet accounts appear in reports immediately.
      currentBalanceInBaseCents: values.openingBalanceCents ?? 0n,
    };
    setAccounts((prev) => [...prev, newAcc]);
    setCreateOpen(false);
    const openingNote =
      values.openingBalanceCents && values.openingBalanceCents !== 0n
        ? " (opening balance seeded)"
        : "";
    toast.success(
      `Account ${newAcc.code} ${newAcc.name} created (mock)${openingNote}`,
    );
  }

  function handleEdit(values: AccountFormValues) {
    if (!editingId) return;
    setAccounts((prev) =>
      prev.map((a) =>
        a._id === editingId
          ? {
              ...a,
              name: values.name.trim(),
              parentAccountId: values.parentAccountId,
              currency: values.currency,
              description: values.description || null,
            }
          : a,
      ),
    );
    setEditingId(null);
    const openingNote =
      values.openingBalanceCents && values.openingBalanceCents !== 0n
        ? " Opening balance queued for posting."
        : "";
    toast.success(`Account updated (mock).${openingNote}`);
  }

  function handleArchive(accountId: string) {
    setAccounts((prev) =>
      prev.map((a) =>
        a._id === accountId ? { ...a, isActive: false } : a,
      ),
    );
    toast.success("Account archived (mock)");
  }

  function handleExportCsv(extension: "csv" | "xls") {
    const columns: ExportColumn<MockAccount>[] = [
      { key: "code", label: "Code" },
      { key: "name", label: "Name" },
      { key: "accountType", label: "Type" },
      { key: "normalBalance", label: "Normal balance" },
      {
        key: "parentAccountId",
        label: "Parent code",
        value: (r) => {
          if (!r.parentAccountId) return "";
          const p = accounts.find((a) => a._id === r.parentAccountId);
          return p?.code ?? "";
        },
      },
      { key: "currency", label: "Currency" },
      { key: "currentBalanceInBaseCents", label: "Balance BDT" },
      { key: "isActive", label: "Active" },
      { key: "description", label: "Description", format: (v) => (v == null ? "" : String(v)) },
    ];
    downloadCsv(
      exportFilename("chart-of-accounts", extension),
      toCsv(visible, columns),
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Chart of Accounts"
        description="The canonical list of every accounting bucket. Hierarchical: parent rows roll up children."
        actions={
          <>
            <ExportButton
              onCsv={() => handleExportCsv("csv")}
              onExcel={() => handleExportCsv("xls")}
            />
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> Add account
            </Button>
          </>
        }
      />

      <section
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4"
        aria-label="CoA totals"
      >
        {(["asset", "liability", "equity", "revenue", "expense"] as const).map(
          (t) => (
            <KpiCard
              key={t}
              caption={t[0]!.toUpperCase() + t.slice(1) + "s"}
              value={formatMoneySmart(totalsByType.get(t) ?? 0n, "BDT", {
                locale: "en-BD",
              })}
              hint={`${accounts.filter((a) => a.accountType === t).length} accounts`}
            />
          ),
        )}
      </section>

      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
        <TabsList>
          {TYPE_TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {TYPE_TABS.map((t) => (
          <TabsContent key={t.value} value={t.value} className="mt-4">
            {roots.length === 0 ? (
              <div className="rounded-md border border-(--border-subtle) bg-(--bg-canvas) p-8 text-center text-[13px] text-(--text-secondary)">
                <FolderTree
                  className="mx-auto mb-2 h-6 w-6 text-(--text-tertiary)"
                  aria-hidden
                />
                No accounts yet. Use Add account to create one.
              </div>
            ) : (
              <div className="rounded-md border border-(--border-subtle) bg-(--bg-canvas) p-1 flex flex-col">
                {roots.map((root) => (
                  <AccountTreeNode
                    key={root._id}
                    account={root}
                    childrenByParent={childrenByParent}
                    onSelect={(a) => setSelectedId(a._id)}
                    selectedId={selectedId ?? undefined}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      <Sheet open={createOpen} onOpenChange={setCreateOpen}>
        <SheetContent side="right" size="md">
          <SheetHeader>
            <SheetTitle>Add account</SheetTitle>
            <SheetDescription>
              New buckets become available in journal-line account pickers.
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <AccountForm
              parentOptions={accounts.map((a) => ({
                id: a._id,
                code: a.code,
                name: a.name,
                accountType: a.accountType,
              }))}
              onSubmit={handleCreate}
              onCancel={() => setCreateOpen(false)}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      <Sheet
        open={selectedId !== null}
        onOpenChange={(o) => {
          if (!o) {
            setSelectedId(null);
            setEditingId(null);
          }
        }}
      >
        <SheetContent side="right" size="md">
          {selected && editingId ? (
            <>
              <SheetHeader>
                <SheetTitle>
                  Edit{" "}
                  <span className="font-mono">{selected.code}</span> {selected.name}
                </SheetTitle>
                <SheetDescription>
                  Code is immutable so journal lines remain stable.
                </SheetDescription>
              </SheetHeader>
              <SheetBody>
                <AccountForm
                  isEdit
                  initialValues={{
                    code: selected.code,
                    name: selected.name,
                    accountType: selected.accountType,
                    parentAccountId: selected.parentAccountId,
                    currency: selected.currency,
                    description: selected.description ?? "",
                  }}
                  parentOptions={accounts.map((a) => ({
                    id: a._id,
                    code: a.code,
                    name: a.name,
                    accountType: a.accountType,
                  }))}
                  onSubmit={handleEdit}
                  onCancel={() => setEditingId(null)}
                />
              </SheetBody>
            </>
          ) : selected ? (
            <>
              <SheetHeader>
                <SheetTitle className="flex flex-wrap items-center gap-3">
                  <span className="font-mono">{selected.code}</span>
                  {selected.name}
                  <AccountTypeBadge type={selected.accountType} />
                </SheetTitle>
                <SheetDescription>
                  {selected.description ?? "No description on file."}
                </SheetDescription>
              </SheetHeader>
              <SheetBody>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <DetailField
                    label="Current balance"
                    value={formatMoney(selected.currentBalanceInBaseCents, "BDT", {
                      locale: "en-BD",
                    })}
                  />
                  <DetailField
                    label="Normal balance"
                    value={selected.normalBalance}
                  />
                  <DetailField label="Currency" value={selected.currency} />
                  <DetailField
                    label="Status"
                    value={selected.isActive ? "Active" : "Archived"}
                  />
                </div>
                <h3 className="text-[13px] font-semibold text-(--text-primary) mb-2">
                  Recent transactions
                </h3>
                <DataTable
                  data={recentTxns}
                  columns={TXN_COLUMNS}
                  getRowId={(r) => r._id}
                  emptyTitle="No recent activity"
                  emptyDescription="This account has no posted lines in the last 90 days."
                />
              </SheetBody>
              <SheetFooter>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setEditingId(selected._id)}
                  aria-label="Edit"
                >
                  <Pencil className="size-4" />
                </Button>
                {selected.isActive ? (
                  <ConfirmPopover
                    trigger={
                      <Button variant="outline">
                        <Archive className="h-4 w-4" /> Archive
                      </Button>
                    }
                    title="Archive this account?"
                    description="Archived accounts can no longer be picked on new journal lines, but historical data is preserved."
                    confirmLabel="Archive"
                    confirmVariant="destructive"
                    onConfirm={() => handleArchive(selected._id)}
                  />
                ) : null}
              </SheetFooter>
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

const TXN_COLUMNS: ColumnDef<ReturnType<typeof linesForAccount>[number]>[] = [
  {
    id: "date",
    header: "Date",
    cell: ({ row }) => (
      <span className="text-(--text-secondary) tabular-nums text-[12px]">
        {formatDate(row.original.entryDate, { style: "short" })}
      </span>
    ),
  },
  {
    id: "ref",
    header: "Entry",
    cell: ({ row }) => (
      <span className="font-mono text-[12px] text-(--text-primary)">
        {row.original.entry.entryNumber || "(draft)"}
      </span>
    ),
    meta: { mono: true },
  },
  {
    id: "desc",
    header: "Description",
    cell: ({ row }) => (
      <span className="text-(--text-secondary) line-clamp-1">
        {row.original.description ?? row.original.entry.description}
      </span>
    ),
  },
  {
    id: "debit",
    header: "Debit",
    meta: { numeric: true, mono: true },
    cell: ({ row }) => (
      <span className="font-mono tabular-nums">
        {row.original.debitInBaseCents > 0n
          ? formatMoney(row.original.debitInBaseCents, "BDT", { locale: "en-BD" })
          : "—"}
      </span>
    ),
  },
  {
    id: "credit",
    header: "Credit",
    meta: { numeric: true, mono: true },
    cell: ({ row }) => (
      <span className="font-mono tabular-nums">
        {row.original.creditInBaseCents > 0n
          ? formatMoney(row.original.creditInBaseCents, "BDT", { locale: "en-BD" })
          : "—"}
      </span>
    ),
  },
];

function DetailField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-(--text-tertiary)">
        {label}
      </div>
      <div className="text-[13px] text-(--text-primary) font-mono tabular-nums capitalize">
        {value}
      </div>
    </div>
  );
}
